import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Star, 
  Building2, 
  CheckSquare, 
  Check, 
  Briefcase, 
  Phone,
  Lock,
  Eye,
  EyeOff,
  Shield,
  CheckCircle2,
  AlertCircle,
  UserCheck
} from 'lucide-react';
import BaseRegister from '../components/register/BaseRegister';
import { toast } from '../components/ui/toast/use-toast';

const UpgradeToSeller = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const totalSteps = 5;
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Estados para verificación de teléfono
  const [verificationState, setVerificationState] = useState({
    loading: false,
    codeSent: false,
    error: null
  });
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  
  // Estados para contraseña y términos
  const [showPassword, setShowPassword] = useState(false);
  const [identityVerified, setIdentityVerified] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  const [formData, setFormData] = useState({
    currentPassword: '',
    experiencia: user?.experiencia || '',
    anos_experiencia: user?.anos_experiencia || '',
    servicios: user?.servicios || [],
    slogan: user?.slogan || '',
    telefono: user?.telefono || '',
    prefijo_telefono: user?.prefijo_telefono || '+51',
    phoneVerified: user?.phone_verified || false
  });

  // Timer para reenvío de código
  useEffect(() => {
    let timer;
    if (timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          const newValue = prev - 1;
          if (newValue <= 0) {
            setResendDisabled(false);
            return 0;
          }
          return newValue;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [timeLeft]);

  // Validar tipo de usuario al cargar el componente
  useEffect(() => {
    if (user && user.tipo_usuario_id !== 1) {
      toast({
        title: "Acceso no permitido",
        description: "No tienes permiso para acceder a esta página",
        variant: "destructive"
      });
      navigate('/configuracion');
    }
  }, [user, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpiar errores
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleServiceChange = (service) => {
    setFormData(prev => {
      const newServices = prev.servicios.includes(service)
        ? prev.servicios.filter(s => s !== service)
        : [...prev.servicios, service];
      return { ...prev, servicios: newServices };
    });
  };

  // Verificar identidad con contraseña actual
  const verifyIdentity = async () => {
    if (!formData.currentPassword) {
      setErrors(prev => ({ ...prev, currentPassword: 'La contraseña es requerida' }));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/auth/verify-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          password: formData.currentPassword
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Contraseña incorrecta');
      }

      setIdentityVerified(true);
      toast({
        title: "Identidad verificada",
        description: "Tu contraseña ha sido verificada correctamente",
        variant: "success"
      });
      
      // Avanzar al siguiente paso
      handleNext();
    } catch (error) {
      console.error('Error en verificación:', error);
      setErrors(prev => ({ 
        ...prev, 
        currentPassword: error.message || 'Contraseña incorrecta' 
      }));
      
      toast({
        title: "Error de verificación",
        description: error.message || "No se pudo verificar la contraseña",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const validateStep = () => {
    const newErrors = {};

    switch (currentStep) {
      case 1: // Verificación de identidad
        if (!formData.currentPassword) {
          newErrors.currentPassword = 'La contraseña es requerida';
        }
        break;
        
      case 2: // Experiencia
        if (!formData.slogan) {
          newErrors.slogan = 'El slogan es requerido';
        }
        if (!formData.experiencia) {
          newErrors.experiencia = 'La descripción de experiencia es requerida';
        }
        if (!formData.anos_experiencia) {
          newErrors.anos_experiencia = 'Los años de experiencia son requeridos';
        }
        break;

      case 3: // Servicios
        if (formData.servicios.length === 0) {
          newErrors.servicios = 'Debe seleccionar al menos un servicio';
        }
        if (formData.servicios.length > 6) {
          newErrors.servicios = 'No puede seleccionar más de 6 servicios';
        }
        break;
        
      case 4: // Verificación de teléfono
        if (!formData.telefono) {
          newErrors.telefono = 'El teléfono es requerido';
        } else if (formData.telefono.length !== 9) {
          newErrors.telefono = 'El teléfono debe tener 9 dígitos';
        } else if (!formData.phoneVerified) {
          newErrors.telefono = 'Debe verificar su número de teléfono';
        }
        break;
        
      case 5: // Términos y condiciones
        if (!acceptedTerms) {
          newErrors.terms = 'Debe aceptar los Términos y Condiciones';
        }
        if (!acceptedPrivacy) {
          newErrors.privacy = 'Debe aceptar la Política de Privacidad';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => prev + 1);
      setErrors({});
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    setErrors({});
  };

  const requestPhoneVerificationCode = async () => {
    // Verificar longitud del teléfono
    if (!formData.telefono || formData.telefono.length !== 9) {
      setErrors(prev => ({ 
        ...prev, 
        telefono: 'El teléfono debe tener 9 dígitos' 
      }));
      return;
    }

    try {
      setLoading(true);
      setVerificationState({
        loading: true,
        codeSent: false,
        error: null
      });

      const response = await fetch('/api/auth/verification/request-sms-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          telefono: formData.telefono,
          prefijo: formData.prefijo_telefono
        })
      });

      // Verificación de errores de red
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: 'Error de respuesta',
          message: `El servidor respondió con estado ${response.status}`
        }));
        
        throw new Error(errorData.message || errorData.error || 'Error al enviar código de verificación');
      }

      const data = await response.json();

      // Actualizar estados
      setPhoneCodeSent(true);
      setTimeLeft(600); // 10 minutos
      setResendDisabled(true);
      setVerificationState({
        loading: false,
        codeSent: true,
        error: null
      });

      toast({
        title: "Código enviado",
        description: "Te hemos enviado un código por SMS",
      });

      // El código se enviará directamente al teléfono del usuario

    } catch (error) {
      console.error('Error al enviar código SMS:', error);
      
      toast({
        title: "Error",
        description: error.message || "No se pudo enviar el código de verificación",
        variant: "destructive"
      });
      
      setVerificationState({
        loading: false,
        codeSent: false,
        error: error.message || 'Error al enviar código'
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyPhoneCode = async (code) => {
    if (code.length !== 6) return;
  
    try {
      setLoading(true);
      
      const response = await fetch('/api/auth/verification/verify-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          telefono: formData.telefono,
          prefijo: formData.prefijo_telefono,
          code: code
        })
      });
  
      // Verificación de errores
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: 'Error de respuesta',
          message: `El servidor respondió con estado ${response.status}`
        }));
        
        throw new Error(errorData.message || errorData.error || 'Código inválido');
      }
  
      const data = await response.json();
  
      if (!data.success) {
        throw new Error(data.error || data.message || 'Error al verificar el código');
      }
  
      setFormData(prev => ({ ...prev, phoneVerified: true }));
      // Limpiar errores de teléfono
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.telefono;
        return newErrors;
      });
      
      setPhoneCodeSent(false);
      setVerificationState({
        loading: false,
        codeSent: false,
        error: null
      });
      
      toast({
        title: "Teléfono verificado",
        description: "Tu número de teléfono ha sido verificado correctamente",
        variant: "success"
      });
  
    } catch (error) {
      console.error('Error en verificación de código:', error);
      
      toast({
        title: "Error",
        description: error.message || "Error al verificar el código",
        variant: "destructive",
      });
      
      setVerificationState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    } finally {
      setLoading(false);
    }
  };

  // Componente para mostrar el estado de la verificación
  const VerificationStatus = () => {
    if (verificationState.loading) {
      return (
        <div className="flex items-center justify-center space-x-2 text-blue-500">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
          <span>Enviando código...</span>
        </div>
      );
    }
    
    if (verificationState.error) {
      return (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0"/>
          <span>{verificationState.error}</span>
        </div>
      );
    }
    
    if (verificationState.codeSent) {
      return (
        <div className="bg-blue-50 text-blue-700 p-3 rounded-lg">
          <p>Te hemos enviado un código de verificación por SMS.</p>
          <p className="mt-1 text-sm">
            Tiempo restante: {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
          </p>
        </div>
      );
    }
    
    return null;
  };

  const handleFinish = async () => {
    if (!validateStep()) return;
  
    setLoading(true);
    try {
      // Preparar los datos según el formato que espera el endpoint existente
      const requestData = {
        slogan: formData.slogan,
        anos_experiencia: parseInt(formData.anos_experiencia),
        experiencia: formData.experiencia,
        servicios: formData.servicios,
        telefono: formData.telefono,
        prefijo_telefono: formData.prefijo_telefono,
        currentPassword: formData.currentPassword,
        phoneVerified: formData.phoneVerified,
        userType: 'vendedor'
      };
  
      console.log('Enviando datos para upgrade a seller:', requestData);
  
      const response = await fetch('https://backend-production-1786.up.railway.app/api/user/upgrade-to-seller', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestData)
      });
  
      // Obtener la respuesta como texto primero
      const responseText = await response.text();
      console.log('Respuesta raw del servidor:', responseText);
  
      // Intentar parsear como JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parseando JSON:', parseError);
        throw new Error(`Respuesta inválida del servidor: ${responseText.substring(0, 100)}...`);
      }
  
      if (!response.ok) {
        throw new Error(data.error || data.details || 'Error en la actualización');
      }
  
      // Mostrar mensaje de éxito
      toast({
        title: "¡Felicidades!",
        description: "Has sido actualizado a vendedor exitosamente. Se cerrará la sesión para aplicar los cambios.",
        variant: "success"
      });
  
      // Esperar 3 segundos para que el usuario vea el mensaje de éxito
      setTimeout(() => {
        // Cerrar sesión
        logout();
        
        // Recargar la página para aplicar los cambios
        window.location.href = '/login';
      }, 3000);
  
    } catch (error) {
      console.error('Error en actualización:', error);
      
      toast({
        title: "Error",
        description: error.message || "No se pudo completar la actualización",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return renderIdentityVerificationStep();
      case 2:
        return renderExperienceStep();
      case 3:
        return renderServicesStep();
      case 4:
        return renderPhoneVerificationStep();
      case 5:
        return renderTermsAndConfirmationStep();
      default:
        return null;
    }
  };
  
  const renderIdentityVerificationStep = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-center mb-4">
        Verificación de Identidad
      </h3>
      
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-6">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
          <p className="text-sm text-blue-700">
            Por seguridad, necesitamos verificar tu identidad antes de continuar con la actualización a vendedor. Por favor, ingresa tu contraseña actual.
          </p>
        </div>
      </div>

      <div className="relative">
        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500" size={20} />
        <input
          type={showPassword ? "text" : "password"}
          placeholder="Contraseña actual"
          name="currentPassword"
          value={formData.currentPassword}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-12 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors.currentPassword ? 'border-red-500 bg-red-50' : 'border-gray-200'
          }`}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-blue-500 transition-colors"
        >
          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
      </div>
      
      {errors.currentPassword && (
        <p className="text-red-500 text-sm bg-red-50 p-2 rounded-lg">
          {errors.currentPassword}
        </p>
      )}
      
      <button
        onClick={verifyIdentity}
        disabled={loading || !formData.currentPassword}
        className={`w-full py-3 rounded-lg transition-all duration-300 ${
          loading || !formData.currentPassword
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <span className="mr-2">Verificando...</span>
            <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
          </div>
        ) : (
          'Verificar identidad'
        )}
      </button>
    </div>
  );

  const renderExperienceStep = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-center mb-4">
        Cuéntanos sobre tu Experiencia
      </h3>

      <div className="relative">
        <label className="block text-sm font-medium text-gray-700">Slogan</label>
        <input
          type="text"
          placeholder="Ej: Tu aliado inmobiliario de confianza"
          name="slogan"
          value={formData.slogan}
          onChange={handleInputChange}
          className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors.slogan ? 'border-red-500' : 'border-gray-200'
          }`}
          maxLength={100}
        />
        <span className="text-xs text-gray-500">
          {formData.slogan.length}/100 caracteres
        </span>
        {errors.slogan && (
          <p className="text-red-500 text-sm mt-1">{errors.slogan}</p>
        )}
      </div>

      <div className="relative">
        <label className="block text-sm font-medium text-gray-700">
          Años de Experiencia
        </label>
        <input
          type="number"
          name="anos_experiencia"
          value={formData.anos_experiencia}
          onChange={handleInputChange}
          min="0"
          max="50"
          className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors.anos_experiencia ? 'border-red-500' : 'border-gray-200'
          }`}
        />
        {errors.anos_experiencia && (
          <p className="text-red-500 text-sm mt-1">{errors.anos_experiencia}</p>
        )}
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Descripción de tu Experiencia
        </label>
        <textarea
          name="experiencia"
          value={formData.experiencia}
          onChange={handleInputChange}
          placeholder="Detalla tu trayectoria profesional en el sector inmobiliario..."
          className={`w-full p-4 border-2 rounded-lg min-h-[120px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            errors.experiencia ? 'border-red-500' : 'border-gray-200'
          }`}
        />
        {errors.experiencia && (
          <p className="text-red-500 text-sm mt-1">{errors.experiencia}</p>
        )}
      </div>
    </div>
  );

  const renderServicesStep = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-center mb-4">
        Selecciona tus Servicios (Máximo 6)
      </h3>

      <div className="grid grid-cols-2 gap-4">
        {[
          'Asesoría inmobiliaria',
          'Evaluación de propiedades',
          'Gestión de documentos',
          'Marketing inmobiliario',
          'Negociación',
          'Servicio post-venta',
          'Visitas guiadas',
          'Fotografía profesional'
        ].map((servicio) => (
          <label
            key={servicio}
            className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
              formData.servicios.includes(servicio) 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <input
              type="checkbox"
              checked={formData.servicios.includes(servicio)}
              onChange={() => handleServiceChange(servicio)}
              className="hidden"
            />
            <CheckSquare
              className={`w-5 h-5 mr-2 ${
                formData.servicios.includes(servicio) 
                  ? 'text-blue-500' 
                  : 'text-gray-400'
              }`}
            />
            <span className="text-sm">{servicio}</span>
          </label>
        ))}
      </div>

      {errors.servicios && (
        <p className="text-red-500 text-sm text-center mt-2">
          {errors.servicios}
        </p>
      )}
    </div>
  );
  
  const renderPhoneVerificationStep = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-center mb-4">
        Verificación de Teléfono
      </h3>
  
      {formData.phoneVerified ? (
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-green-500" />
            <div>
              <h4 className="font-medium text-green-800">Teléfono verificado correctamente</h4>
              <p className="text-green-600 text-sm">{formData.prefijo_telefono} {formData.telefono}</p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex gap-2">
            <select
              value={formData.prefijo_telefono}
              onChange={(e) => setFormData(prev => ({ ...prev, prefijo_telefono: e.target.value }))}
              className="pl-2 pr-8 py-2 border-2 rounded-lg bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            >
              <option value="+51">🇵🇪 +51</option>
              <option value="+56">🇨🇱 +56</option>
              <option value="+57">🇨🇴 +57</option>
              <option value="+54">🇦🇷 +54</option>
              <option value="+593">🇪🇨 +593</option>
            </select>
  
            <div className="relative flex-1">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500" size={20} />
              <input
                type="tel"
                placeholder="Número de teléfono"
                name="telefono"
                value={formData.telefono}
                onChange={(e) => {
                  // Permitir solo números y limitar a 9 dígitos
                  const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                  setFormData(prev => ({ 
                    ...prev, 
                    telefono: value 
                  }));
                  
                  // Limpiar errores
                  if (errors.telefono) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.telefono;
                      return newErrors;
                    });
                  }
                }}
                className={`w-full pl-10 pr-4 py-2 border-2 rounded-lg transition-colors ${
                  errors.telefono ? 'border-red-500 bg-red-50' : 'border-gray-200'
                }`}
              />
            </div>
          </div>
  
          {errors.telefono && (
            <p className="text-red-500 text-sm bg-red-50 p-2 rounded-lg">
              {errors.telefono}
            </p>
          )}
  
          <VerificationStatus />
  
          {/* El usuario recibirá el código en su teléfono */}
  
          <button
            onClick={requestPhoneVerificationCode}
            disabled={loading || phoneCodeSent || formData.telefono.length !== 9}
            className={`w-full py-2 rounded-lg transition-all ${
              (loading || phoneCodeSent || formData.telefono.length !== 9) 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            {phoneCodeSent 
              ? 'Código enviado' 
              : loading 
                ? (
                  <div className="flex items-center justify-center">
                    <span className="mr-2">Enviando...</span>
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  </div>
                ) 
                : 'Enviar código de verificación'}
          </button>
  
          {phoneCodeSent && (
            <div className="space-y-3">
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700 mb-3 text-center">
                  Ingresa el código de 6 dígitos enviado a tu teléfono
                </p>
                <input
                  type="text"
                  placeholder="Código de verificación"
                  value={phoneVerificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setPhoneVerificationCode(value);
                  }}
                  className="w-full px-4 py-2 border-2 rounded-lg text-center text-lg tracking-wider focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                  maxLength="6"
                />
                
                <button
                  onClick={() => {
                    if (phoneVerificationCode.length === 6) {
                      verifyPhoneCode(phoneVerificationCode);
                    }
                  }}
                  disabled={phoneVerificationCode.length !== 6 || verificationState.loading}
                  className={`w-full mt-3 py-2 rounded-lg transition-colors ${
                    phoneVerificationCode.length !== 6 || verificationState.loading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {verificationState.loading ? 'Verificando...' : 'Verificar código'}
                </button>
              </div>
              
              {timeLeft > 0 && (
                <p className="text-sm text-gray-500 text-center">
                  El código expirará en: <span className="font-medium">{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</span>
                </p>
              )}
            </div>
          )}
        </>
      )}
      
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
        <div className="flex items-start gap-3">
          <UserCheck className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            La verificación de tu teléfono aumenta la seguridad de tu cuenta y permite a los clientes contactarte más fácilmente. Tu número será visible en tus publicaciones.
          </p>
        </div>
      </div>
    </div>
  );
  
  const renderTermsAndConfirmationStep = () => (
    <div className="text-center space-y-6">
      <Star className="w-16 h-16 mx-auto text-yellow-500" />
      <h3 className="text-xl font-bold">¡Estás a un paso de convertirte en Vendedor!</h3>
      
      <div className="bg-white p-4 rounded-lg shadow-md">
        <h4 className="font-semibold mb-2">Resumen de tu Perfil</h4>
        <p className="text-gray-700">
          <strong>Slogan:</strong> {formData.slogan}
        </p>
        <p className="text-gray-700">
          <strong>Años de Experiencia:</strong> {formData.anos_experiencia}
        </p>
        <p className="text-gray-700">
          <strong>Servicios:</strong> {formData.servicios.join(', ')}
        </p>
        <p className="text-gray-700">
          <strong>Teléfono verificado:</strong> {formData.prefijo_telefono} {formData.telefono}
        </p>
      </div>
      
      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="font-semibold mb-2 text-blue-700">
          Beneficios de Inkayros.com
        </h4>
        <p className="text-gray-700 mb-4">
          Te hemos asignado automáticamente al plan Gratuito, que te permite 
          publicar tu primera propiedad sin costo. Disfruta de la experiencia 
          de Inkayros.com y expande tu negocio inmobiliario.
        </p>
        <div className="flex items-center justify-center space-x-2 text-green-600">
          <Check className="w-5 h-5" />
          <span className="font-medium">Primera publicación gratis</span>
        </div>
      </div>
      
      <div className="space-y-4 mt-6 bg-gray-50 p-6 rounded-xl border border-gray-200">
        <h4 className="font-medium text-gray-800">Términos Legales</h4>
        <p className="text-sm text-gray-600">
          Para completar tu registro, es necesario que aceptes nuestros términos legales:
        </p>
        
        <div className="space-y-3">
          {/* Checkbox para Términos y Condiciones */}
          <div className={`flex items-start gap-3 ${errors.terms ? 'pb-1' : ''}`}>
            <div className="flex items-center h-5 mt-1">
              <input
                id="terms"
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className={`h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                  errors.terms ? 'border-red-300' : ''
                }`}
              />
            </div>
            <div>
              <label htmlFor="terms" className={`text-sm font-medium ${errors.terms ? 'text-red-700' : 'text-gray-700'}`}>
                Acepto los <a 
                  href="/terminos" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('/terminos', '_blank');
                  }}
                >
                  Términos y Condiciones
                </a>
              </label>
              {errors.terms && (
                <p className="text-sm text-red-600">{errors.terms}</p>
              )}
            </div>
          </div>
          
          {/* Checkbox para Política de Privacidad */}
          <div className={`flex items-start gap-3 ${errors.privacy ? 'pb-1' : ''}`}>
            <div className="flex items-center h-5 mt-1">
              <input
                id="privacy"
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                className={`h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                  errors.privacy ? 'border-red-300' : ''
                }`}
              />
            </div>
            <div>
              <label htmlFor="privacy" className={`text-sm font-medium ${errors.privacy ? 'text-red-700' : 'text-gray-700'}`}>
                Acepto la <a 
                  href="/privacidad" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open('/privacidad', '_blank');
                  }}
                >
                  Política de Privacidad
                </a>
              </label>
              {errors.privacy && (
                <p className="text-sm text-red-600">{errors.privacy}</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="text-xs text-gray-500 pt-2">
          Al aceptar, confirmas que has leído y estás de acuerdo con nuestros términos legales y entiendes cómo procesamos tus datos como vendedor en la plataforma.
        </div>
      </div>
    </div>
  );

  return (
    <BaseRegister
      currentStep={currentStep}
      totalSteps={totalSteps}
      handleNext={handleNext}
      handleBack={handleBack}
      handleFinish={handleFinish}
      loading={loading}
      title="Conviértete en Vendedor"
      errors={errors}
    >
      {renderStep()}
    </BaseRegister>
  );
};

export default UpgradeToSeller;